import { type CaseTypeRepository, Workspace, type WorkspaceRepository } from '@telespot/domain';

export interface RetrieveWorkspaceOutput {
  id: string;
  name: string;
  organizationId: string;
  caseTypeId: string;
  caseTypeName: string;
  numCases: number;
  numMembers: number;
  usedStorage: number;
  config: Record<string, unknown>;
  referenceDate: Date;
}

/**
 * Use case to retrieve a workspace by its ID.
 */
export interface IRetrieveWorkspaceQuery {
  /**
   * Retrieve a workspace by its ID.
   * @param workspaceId The ID of the workspace to retrieve.
   * @returns The workspace. Undefined if the workspace does not exist or the operation fails.
   */
  execute(workspaceId: string): Promise<RetrieveWorkspaceOutput | undefined>;
}

export class RetrieveWorkspaceQuery implements IRetrieveWorkspaceQuery {
  constructor(
    private readonly workspaceRepository: WorkspaceRepository,
    private readonly caseTypeRepository: CaseTypeRepository
  ) {}

  private async getWorkspace(workspaceId: string): Promise<Workspace | undefined> {
    try {
      return await this.workspaceRepository.getById(workspaceId);
    } catch (error) {
      console.error('Error retrieving workspace:', error);
      return undefined;
    }
  }

  private async getCaseTypeName(caseTypeId: string | undefined): Promise<string> {
    if (!caseTypeId) {
      return '';
    }

    try {
      const caseType = await this.caseTypeRepository.getById(caseTypeId);
      return caseType?.name ?? '';
    } catch (error) {
      console.error('Error retrieving case type:', error);
      return '';
    }
  }

  public async execute(workspaceId: string): Promise<RetrieveWorkspaceOutput | undefined> {
    const workspace = await this.getWorkspace(workspaceId);
    if (!workspace) return undefined;

    const caseTypeName = await this.getCaseTypeName(workspace.caseTypeId);

    return {
      id: workspace.id,
      name: workspace.name,
      organizationId: workspace.organizationId,
      caseTypeId: workspace.caseTypeId,
      caseTypeName,
      numCases: workspace.numCases,
      numMembers: workspace.numMembers,
      usedStorage: workspace.usedStorage,
      config: workspace.config,
      referenceDate: workspace.updatedAt ?? workspace.createdAt ?? new Date(),
    };
  }
}
